// Primary auth hook — wraps the Zustand store for component consumption.
// Use this in all components instead of importing useAuthStore directly.

import { useCallback } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { MFA_REQUIRED_ROLES } from '@/constants/auth';
import type { AdminRole } from '@/types/auth';

export function useAuth() {
  const admin = useAuthStore((s) => s.admin);
  const accessToken = useAuthStore((s) => s.accessToken);
  const mfaToken = useAuthStore((s) => s.mfaToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isLoading = useAuthStore((s) => s.isLoading);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setMfaToken = useAuthStore((s) => s.setMfaToken);
  const clearMfaToken = useAuthStore((s) => s.clearMfaToken);
  const logout = useAuthStore((s) => s.logout);
  const refreshAdmin = useAuthStore((s) => s.refreshAdmin);

  /** Returns true if the given role requires TOTP MFA (super_admin, finance_admin). */
  const requiresMfa = useCallback(
    (role: AdminRole): boolean => (MFA_REQUIRED_ROLES as readonly string[]).includes(role),
    [],
  );

  /** Returns true if the current admin has at least one of the specified roles. */
  const hasRole = useCallback(
    (...roles: AdminRole[]): boolean => {
      if (!admin) return false;
      return roles.includes(admin.role);
    },
    [admin],
  );

  return {
    admin,
    accessToken,
    mfaToken,
    isAuthenticated,
    isInitialized,
    isLoading,
    requiresMfa,
    hasRole,
    setTokens,
    setMfaToken,
    clearMfaToken,
    logout,
    refreshAdmin,
  };
}
